import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	Output,
	ViewChild,
	EventEmitter,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import {
	DataSetComponent,
	DropdownOption,
} from 'src/app/components/data-set/data-set.component';
import { Company } from 'src/app/models/data/company';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserWithRoleName } from 'src/app/models/data/user';
import { UserProfile } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DeactivateUserDialogComponent } from 'src/app/components/deactivate-user-dialog/deactivate-user-dialog.component';
import {
	CannotSetActiveStatusErrorDialogComponent,
	LastUserErrorDialogComponent,
} from '../../roles/role-detail/role-detail-user-table/role-detail-user-table.component';
import { ReactivateUserDialogComponent } from 'src/app/components/reactivate-user-dialog/reactivate-user-dialog.component';
import { WhitelistScreens } from '../user-main-tab/user-main-tab.component';
import { SelectedButton } from 'src/app/components/button-toggle/button-toggle.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ButtonToggleComponent } from '../../../components/button-toggle/button-toggle.component';
import {
	DataSetTableComponent,
	DataSetTableMenuComponent,
	DataSetRowClickDirective,
} from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { PhoneNumberPipe } from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-user-directory',
	templateUrl: './user-directory.component.html',
	styleUrls: ['./user-directory.component.scss', '../../common/main.scss'],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		MatSlideToggle,
		ButtonToggleComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		DataSetTableMenuComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetRowClickDirective,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		PhoneNumberPipe,
		DataSetLoadingRowComponent,
	],
})
export class UserDirectoryComponent implements AfterViewInit, OnDestroy {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@Input() showWhitelistedUsers: boolean = false;
	/** When set, loads users for this company instead of the caller's active company. */
	@Input() companyId?: string;
	/** When true, row navigation and write guards follow company-detail embed rules. */
	@Input() embeddedInCompanyDetail = false;
	/** Company context for deactivate/reactivate dialogs when embedded. */
	@Input() company?: Company;
	@Output() whitelistPageSelectionChanged =
		new EventEmitter<WhitelistScreens>();

	displayedColumns: string[] = [
		'name',
		'email',
		'phoneNumber',
		'active',
		'roleName',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<UserWithRoleName>();
	hasUserRoleManagement?: boolean;
	hasGlobalCompanyMgmt?: boolean;
	userProfile?: UserProfile;
	canEditRoles?: boolean;
	/** Controls Add / inactive-toggle visibility (URM on /users; CompanyUser SentriLock rules when embedded). */
	canShowUserManagementActions = false;
	showAllUsers = false;
	currentFilters: QueryParam[] = [];
	activeCompanyId: string = '';
	private resolvedCompanyId: string = '';

	selectedButton: SelectedButton = SelectedButton.right;

	statusFilterOptions: DropdownOption[] = [
		{ label: $localize`:@@userDirectory-active:Active`, value: '1' },
		{ label: $localize`:@@userDirectory-inactive:Inactive`, value: '0' },
	];

	filterLabels = {
		defaultPlaceholder: $localize`:@@userDirectory-defaultPlaceholder:Search name...`,
		name: $localize`:@@userDirectory-filterLabelName:Name`,
		email: $localize`:@@userDirectory-filterLabelEmail:Email`,
		phoneNumber: $localize`:@@userDirectory-filterLabelPhoneNumber:Phone Number`,
		status: $localize`:@@userDirectory-filterLabelStatus:Status`,
		roleName: $localize`:@@userDirectory-filterLabelRoleName:Role Name`,
	};

	private filterSubscription?: Subscription;

	getCompanyUsersByCompanyIdLoadingKey =
		ApiLoadingKeys.getCompanyUsersByCompanyId;
	resendUserRegistrationLoadingKey = ApiLoadingKeys.resendUserRegistration;
	finishedLoadingInitData: boolean = false;

	constructor(
		private apiService: ApiService,
		private router: Router,
		private scService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService,
		private readonly dialog: MatDialog
	) {
		this.hasUserRoleManagement = this.scService.has(
			SystemCapabilityEnum.userRoleManagement
		);
		this.hasGlobalCompanyMgmt = this.scService.has(
			SystemCapabilityEnum.globalCompanyManagement
		);
	}

	async ngAfterViewInit() {
		const profilePromise = this.apiService.getUserProfile();
		const isEmbedded = this.embeddedInCompanyDetail && !!this.companyId;

		if (isEmbedded) {
			this.resolvedCompanyId = this.companyId!;
			this.subscribeToFilters();
		}

		this.userProfile = await profilePromise;
		this.activeCompanyId =
			this.userProfile?.activeRoleDetails?.company?.id ?? '';
		this.resolvedCompanyId = this.companyId ?? this.activeCompanyId;
		await this.setCanEditRoles();

		if (!isEmbedded) {
			this.subscribeToFilters();
		}
	}

	private subscribeToFilters(): void {
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					this.resolvedCompanyId = this.companyId ?? this.activeCompanyId;
					this.currentFilters = filter;
					const result = await this.getUsers(filter, this.resolvedCompanyId);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((users) => {
				this.dataSource.data = users;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	get embeddedWritesBlocked(): boolean {
		return this.embeddedInCompanyDetail && this.canEditRoles !== true;
	}

	navigateToAddUser(): void {
		if (this.embeddedWritesBlocked) return;
		if (this.embeddedInCompanyDetail && this.resolvedCompanyId) {
			this.router
				.navigate(['/add-user'], {
					queryParams: { companyId: this.resolvedCompanyId },
				})
				.then((_) => {});
			return;
		}
		this.router.navigate(['/add-user']).then((_) => {});
	}

	async showInactiveUsers() {
		this.dataSource.data = await this.getUsers(
			this.currentFilters,
			this.resolvedCompanyId
		);
	}

	async getUsers(filter: QueryParam[], activeCompanyId: string) {
		let queryParameters;
		const whitelistUsersFilter = this.showWhitelistedUsers
			? [{ param: 'isWhitelisted', value: 1 }]
			: [];
		if (this.showAllUsers) {
			queryParameters = [
				...filter,
				...whitelistUsersFilter,
				{ param: 'sortBy', value: 'active' },
				{ param: 'sortDirection', value: 'asc' },
			];
		} else {
			const isActive = filter?.find((param) => param.param === 'active');
			queryParameters = [
				...filter,
				...whitelistUsersFilter,
				{ param: 'active', value: isActive?.value ?? 1 },
			];
		}
		return await this.apiService.getCompanyUsersByCompanyId(
			activeCompanyId!,
			queryParameters
		);
	}

	onRowClick(row: UserWithRoleName): void {
		if (this.embeddedInCompanyDetail && this.resolvedCompanyId) {
			this.router.navigate([
				`/company/${this.resolvedCompanyId}/detail/user/${row.user.id}/detail`,
			]);
			return;
		}
		this.router.navigate(['/user-detail', row.user.id]);
	}

	async onResendRegistrationClick(user: UserWithRoleName): Promise<void> {
		if (this.embeddedWritesBlocked) return;
		await this.apiService.resendUserRegistration(user.user.id);
	}

	async setCanEditRoles(): Promise<void> {
		const sentrilockId = await this.apiService.getSentrilockCompanyId();
		const activeCompanyId = this.userProfile?.activeRoleDetails?.company?.id;
		const editingCompanyId = this.companyId ?? activeCompanyId;
		const companyIsSentrilock = editingCompanyId === sentrilockId;

		if (this.embeddedInCompanyDetail) {
			const isEditingActiveCompany = activeCompanyId === editingCompanyId;
			// Match CompanyUser SentriLock guard — no back door for internal company.
			this.canEditRoles =
				(!!this.hasUserRoleManagement && isEditingActiveCompany) ||
				(!!this.hasGlobalCompanyMgmt && !companyIsSentrilock);
			this.canShowUserManagementActions = !!this.canEditRoles;
			return;
		}

		this.canEditRoles =
			this.hasUserRoleManagement ||
			(this.hasGlobalCompanyMgmt && companyIsSentrilock);
		this.canShowUserManagementActions = !!this.hasUserRoleManagement;
	}

	canAddUser(userWithRoleName: UserWithRoleName): boolean {
		if (this.embeddedWritesBlocked) return false;
		if (userWithRoleName.roleIsUniversal) {
			// Oracles can always remove users
			if (this.scService.isOracle) {
				return true;
			}
			// Only Oracles can remove Oracles
			if (userWithRoleName.isOracle) {
				return false;
			}
			// Only CAs can remove other CAs
			if (userWithRoleName.isCompanyAdmin) {
				return this.scService.isCompanyAdmin;
			}

			// Shouldn't get here, since this.role.isUniversal should either be Oracle or CA
			console.error(
				'Unexpected code path! Role is Universal but not Oracle or Company Admin!'
			);
			return (
				userWithRoleName.roleId === this.userProfile?.activeRoleDetails?.role.id
			);
		} else {
			// If not a Universal role, then the standard canEditRoles checks apply
			if (userWithRoleName.isOracle && !this.scService.isOracle) {
				return false;
			}
			if (
				userWithRoleName.isCompanyAdmin &&
				!this.scService.isCompanyAdmin &&
				!this.hasGlobalCompanyMgmt
			) {
				return false;
			}
			return this.canEditRoles === true;
		}
	}

	canRemoveUser(userWithRoleName: UserWithRoleName): boolean {
		// A user can never remove themselves from a role nor can they remove inactive users
		if (
			userWithRoleName.user.id === this.userProfile?.user?.id ||
			!userWithRoleName.user.active
		) {
			return false;
		}

		// Other checks are the same as adding users
		return this.canAddUser(userWithRoleName);
	}

	canReactivateUser(userWithRoleName: UserWithRoleName): boolean {
		// A user can never re-activate themselves (not that that would theoretically happen) nor can they re-activate already active users
		if (
			userWithRoleName.user.id === this.userProfile?.user?.id ||
			userWithRoleName.user.active
		) {
			return false;
		}

		return this.canAddUser(userWithRoleName);
	}

	async removeUser(userWithRoleName: UserWithRoleName) {
		if (this.embeddedWritesBlocked) return;
		if (userWithRoleName.roleId) {
			// A user should be unable to delete the last user in a role
			const filters: QueryParam[] = [
				{ param: 'rowIndex', value: 0 },
				{ param: 'rowCount', value: 76 },
				{ param: 'active', value: 1 },
			];
			const usersInRole = await this.apiService.getAssignedRoleUsers(
				userWithRoleName.roleId,
				this.resolvedCompanyId ||
					this.userProfile!.activeRoleDetails!.company.id,
				filters
			);

			if (userWithRoleName.roleIsUniversal && 1 >= usersInRole.length) {
				const dialogRef: MatDialogRef<LastUserErrorDialogComponent> =
					this.dialog.open(LastUserErrorDialogComponent, {
						maxWidth: '510px',
						panelClass: 'pop-up-dialog-v2',
					});
			} else {
				this.openDeactivateUserDialog(userWithRoleName);
			}
		} else {
			const dialogRef: MatDialogRef<CannotSetActiveStatusErrorDialogComponent> =
				this.dialog.open(CannotSetActiveStatusErrorDialogComponent, {
					maxWidth: '510px',
					panelClass: 'pop-up-dialog-v2',
					data: {
						activatingUser: false,
					},
				});
		}
	}

	async reactivateUser(userWithRoleName: UserWithRoleName) {
		if (this.embeddedWritesBlocked) return;
		if (userWithRoleName.roleId) {
			this.openReactivateUserDialog(userWithRoleName);
		} else {
			const dialogRef: MatDialogRef<CannotSetActiveStatusErrorDialogComponent> =
				this.dialog.open(CannotSetActiveStatusErrorDialogComponent, {
					maxWidth: '510px',
					panelClass: 'pop-up-dialog-v2',
					data: {
						activatingUser: true,
					},
				});
		}
	}

	openDeactivateUserDialog(userWithRoleName: UserWithRoleName) {
		if (this.embeddedWritesBlocked) return;
		const dialogRef: MatDialogRef<DeactivateUserDialogComponent> =
			this.dialog.open(DeactivateUserDialogComponent, {
				maxWidth: '510px',
				panelClass: 'pop-up-dialog-v2',
				data: {
					users: [userWithRoleName.user],
					roleName: userWithRoleName.roleName,
					company: this.company ?? this.userProfile?.activeRoleDetails?.company,
					roleId: userWithRoleName.roleId,
					openFromUserPage: true,
				},
			});
		this.dataSet.clearAllSelections();

		dialogRef.afterClosed().subscribe((result: any) => {
			if (!!result) {
				this.dataSet.clearAllSelections();
				this.dataSet.applyFilters();
			}
		});
	}

	openReactivateUserDialog(userWithRoleName: UserWithRoleName) {
		if (this.embeddedWritesBlocked) return;
		const dialogRef: MatDialogRef<ReactivateUserDialogComponent> =
			this.dialog.open(ReactivateUserDialogComponent, {
				maxWidth: '510px',
				panelClass: 'pop-up-dialog-v2',
				data: {
					users: [userWithRoleName.user],
					roleName: userWithRoleName.roleName,
					company: this.company ?? this.userProfile?.activeRoleDetails?.company,
					roleId: userWithRoleName.roleId,
				},
			});
		this.dataSet.clearAllSelections();

		dialogRef.afterClosed().subscribe((result: any) => {
			if (!!result) {
				this.dataSet.clearAllSelections();
				this.dataSet.applyFilters();
			}
		});
	}

	getSCService() {
		return this.scService;
	}

	getApiService() {
		return this.apiService;
	}

	getDialog() {
		return this.dialog;
	}

	whitelistPageChanged(selectedButton: SelectedButton) {
		if (selectedButton === SelectedButton.left) {
			this.whitelistPageSelectionChanged.emit(
				WhitelistScreens.whitelistDevices
			);
		} else {
			this.whitelistPageSelectionChanged.emit(WhitelistScreens.whitelistUsers);
		}
	}
	rowId(row: UserWithRoleName): string {
		return row?.user?.id || 'unknown-id';
	}
}
